import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import SearchCategory from "pagesV2/pro/Experiences/Home/TripSearchBox"
import styles from "../index.module.css"

const SEARCH_CATEGORIES = ["publishedTrips", "drafts", "deactivatedTrips"]
const MIN_CHARS = 3

const NoResultsBox = ({ children }) => (
  <div className={styles.noResults}>{children}</div>
)

NoResultsBox.propTypes = {
  children: PropTypes.node.isRequired
}

const SearchResults = ({ showSearchResults, searchQuery, debouncedSearchQuery, searchLoading, searchTrips, categorizedSearchResults, onTripClick, selectedTrip }) => {
  const { t } = useTranslation()

  const trimmed = searchQuery.trim()
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_CHARS

  // The search only fires after the debounce settles. Until the debounced value
  // catches up to what the user just typed, no request has run yet — treat that
  // window as "loading" so we don't flash "no result found" while still typing.
  const debouncePending = trimmed.length >= MIN_CHARS && trimmed !== (debouncedSearchQuery || "").trim()

  const hasCategoryResults = useMemo(
    () => Object.values(categorizedSearchResults).some((list) => list.length > 0),
    [categorizedSearchResults]
  )

  if (!showSearchResults || trimmed.length < MIN_CHARS) {
    if (!tooShort) return null
    return (
      <div className={styles.searchResults}>
        <NoResultsBox>{t("global.type_at_least_characters", { count: MIN_CHARS })}</NoResultsBox>
      </div>
    )
  }

  if (searchLoading || debouncePending) {
    return (
      <fieldset className={styles.searchResults} aria-label={t("global.search_results")}>
        <NoResultsBox>{t("loading")}</NoResultsBox>
      </fieldset>
    )
  }

  if (!searchTrips?.length || !hasCategoryResults) {
    return (
      <fieldset className={styles.searchResults} aria-label={t("global.search_results")}>
        <NoResultsBox>{t("global.no_result_found")}</NoResultsBox>
      </fieldset>
    )
  }

  return (
    <fieldset className={styles.searchResults} aria-label={t("global.search_results")}>
      {SEARCH_CATEGORIES.map(
        (cat) =>
          categorizedSearchResults[cat].length > 0 && (
            <SearchCategory
              key={cat}
              title={t(`experiences.empty_state.${cat.replace("Trips", "")}`)}
              trips={categorizedSearchResults[cat]}
              onTripClick={onTripClick}
              selectedTrip={selectedTrip}
            />
          )
      )}
    </fieldset>
  )
}

SearchResults.propTypes = {
  showSearchResults: PropTypes.bool.isRequired,
  searchQuery: PropTypes.string.isRequired,
  debouncedSearchQuery: PropTypes.string,
  searchLoading: PropTypes.bool,
  searchTrips: PropTypes.array,
  categorizedSearchResults: PropTypes.object.isRequired,
  onTripClick: PropTypes.func.isRequired,
  selectedTrip: PropTypes.object
}

export default SearchResults
